/**
 * FeatureRequestsListPage — landing page for the Feature Request module.
 *
 * Lists drafts + submitted requests, filterable by status/classification/
 * mandatory. The route mounts under /feature-requests. SiteCentric admins
 * see everything (cross-tenant); tenant viewers see only their own.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Sparkles, Plus, AlertTriangle, FlaskConical,
    CheckCircle2, Clock, Tag, Link2,
} from "lucide-react";
import { toast } from "sonner";

const CLASSIF_BADGES = {
    Critical: "bg-rose-500/20 text-rose-200 border-rose-300/30",
    High:     "bg-amber-500/20 text-amber-200 border-amber-300/30",
    Medium:   "bg-violet-500/20 text-violet-200 border-violet-300/30",
    Low:      "bg-sky-500/20 text-sky-200 border-sky-300/30",
    Minimal:  "bg-white/10 text-white/60 border-white/20",
};

const STATUS_BADGES = {
    draft:     { label: "Draft",     cls: "bg-white/10 text-white/70 border-white/20" },
    submitted: { label: "Submitted", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-300/30" },
};

export default function FeatureRequestsListPage() {
    const [items, setItems] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [status, setStatus] = useState("");
    const [classification, setClassification] = useState("");
    const [onlyMandatory, setOnlyMandatory] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchList = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (status) params.set("status", status);
            if (classification) params.set("classification", classification);
            if (onlyMandatory) params.set("mandatory", "true");
            params.set("limit", "100");
            const [{ data: list }, { data: stats }] = await Promise.all([
                api.get(`/feature-requests?${params.toString()}`),
                api.get(`/feature-requests/_/analytics`),
            ]);
            setItems(list || []);
            setAnalytics(stats);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchList(); }, [status, classification, onlyMandatory]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="space-y-8" data-testid="feature-requests-page">
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-4xl">Feature Requests</h1>
                    <p className="text-sm text-white/55 mt-1 max-w-2xl">
                        Submit, clarify, score, and track product feature requests. AI helps non-technical users
                        define what they need; YouTrack + Monday.com get cross-linked records on submit.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            const url = `${window.location.origin}/submit-feature-request`;
                            navigator.clipboard?.writeText(url).then(
                                () => toast.success("Public submission link copied — share with anyone."),
                                () => toast.message(url),
                            );
                        }}
                        data-testid="feature-requests-copy-public-link"
                        className="px-3 py-2 text-xs border border-white/15 hover:bg-white/5 text-white/70 rounded-md inline-flex items-center gap-1.5"
                        title="Copy the public submission URL — works without login.">
                        <Link2 className="w-3.5 h-3.5" /> Copy public link
                    </button>
                    <Link to="/feature-requests/submit"
                          data-testid="feature-requests-new-btn"
                          className="px-4 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm rounded-md inline-flex items-center gap-2 transition-colors">
                        <Plus className="w-4 h-4" /> New feature request
                    </Link>
                </div>
            </header>

            {analytics ? (
                <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="feature-requests-analytics">
                    <StatCard label="Submitted" value={analytics.submitted} icon={CheckCircle2} tone="emerald" />
                    <StatCard label="Drafts" value={analytics.drafts} icon={Clock} tone="white" />
                    <StatCard label="Mandatory" value={analytics.mandatory} icon={AlertTriangle} tone="rose" />
                    <StatCard label="Integration failures" value={analytics.integrationFailures} icon={FlaskConical}
                              tone={analytics.integrationFailures > 0 ? "rose" : "white"} />
                </section>
            ) : null}

            <section className="flex gap-2 flex-wrap" data-testid="feature-requests-filters">
                <Filter value={status} setValue={setStatus} label="All statuses"
                        options={[["", "All statuses"], ["draft", "Draft"], ["submitted", "Submitted"]]}
                        testId="filter-status" />
                <Filter value={classification} setValue={setClassification} label="Any classification"
                        options={[["", "Any classification"], ["Critical", "Critical"], ["High", "High"],
                                  ["Medium", "Medium"], ["Low", "Low"], ["Minimal", "Minimal"]]}
                        testId="filter-classification" />
                <label className="px-3 py-1.5 text-xs border border-white/15 rounded-md inline-flex items-center gap-2 cursor-pointer hover:bg-white/5"
                       data-testid="filter-mandatory">
                    <input type="checkbox" checked={onlyMandatory}
                           onChange={(e) => setOnlyMandatory(e.target.checked)} />
                    Mandatory only
                </label>
            </section>

            {loading ? (
                <div className="text-sm text-white/45">Loading…</div>
            ) : items.length === 0 ? (
                <EmptyState />
            ) : (
                <ul className="space-y-2" data-testid="feature-requests-list">
                    {items.map((it) => <ListRow key={it.featureRequestId} item={it} />)}
                </ul>
            )}
        </div>
    );
}

function StatCard({ label, value, icon: Icon, tone }) {
    const toneCls = tone === "emerald" ? "text-emerald-300"
                  : tone === "rose" ? "text-rose-300"
                  : "text-white/80";
    return (
        <div className="border border-white/10 rounded-xl p-4 bg-white/[0.02]">
            <div className="text-xs text-white/45 uppercase tracking-wide flex items-center gap-1.5">
                <Icon className={`w-3.5 h-3.5 ${toneCls}`} /> {label}
            </div>
            <div className={`text-3xl font-display mt-1 ${toneCls}`}>{value ?? 0}</div>
        </div>
    );
}

function Filter({ value, setValue, options, testId }) {
    return (
        <select value={value} onChange={(e) => setValue(e.target.value)}
                data-testid={testId}
                className="px-3 py-1.5 text-xs bg-white/5 border border-white/15 rounded-md focus:outline-none focus:border-violet-400/60">
            {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
    );
}

function EmptyState() {
    return (
        <div className="border border-white/10 rounded-xl p-10 text-center bg-white/[0.02]"
             data-testid="feature-requests-empty">
            <Sparkles className="w-8 h-8 text-violet-300 mx-auto mb-3" />
            <div className="text-base">No feature requests yet</div>
            <div className="text-sm text-white/50 mt-1">
                Submit your first one — describe it in plain language, the AI will help structure it.
            </div>
            <Link to="/feature-requests/submit"
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm rounded-md transition-colors">
                <Plus className="w-4 h-4" /> Submit a feature request
            </Link>
        </div>
    );
}

function ListRow({ item }) {
    const score = item.evaluation?.score ?? 0;
    const classif = item.evaluation?.classification || "Minimal";
    const isMandatory = item.mandatoryOverride?.enabled;
    const status = STATUS_BADGES[item.status] || STATUS_BADGES.draft;
    const title = item.definedRequest?.approvedTitle || item.originalRequest?.title || "(untitled)";
    const requester = item.source?.requesterName || "—";
    const updated = item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "—";

    return (
        <li className="border border-white/10 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
            data-testid={`feature-request-row-${item.featureRequestId}`}>
            <Link to={`/feature-requests/${item.featureRequestId}`} className="block p-4">
                <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-2 py-0.5 rounded border ${status.cls}`}>{status.label}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded border ${CLASSIF_BADGES[classif]}`}>
                                {classif}
                            </span>
                            {isMandatory ? (
                                <span className="text-[10px] px-2 py-0.5 rounded border border-rose-300/30 bg-rose-500/20 text-rose-200 inline-flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> MANDATORY
                                </span>
                            ) : null}
                            {(item.definedRequest?.tags || []).slice(0, 3).map((t) => (
                                <span key={t} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/55 inline-flex items-center gap-1">
                                    <Tag className="w-2.5 h-2.5" /> {t}
                                </span>
                            ))}
                        </div>
                        <div className="text-sm font-medium mt-1.5 truncate">{title}</div>
                        <div className="text-xs text-white/50 mt-1">
                            {requester} · {item.source?.department || "no dept"} · {updated}
                        </div>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="text-2xl font-display">{score}</div>
                        <div className="text-[10px] text-white/40">/ 100</div>
                    </div>
                </div>
            </Link>
        </li>
    );
}
